import { useCallback, useEffect, useRef, useState } from 'react';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { ActionData } from '@bonusly/common-action-input';
import { useDisclosure } from '@bonusly/design-system';
import { useCurrentCompany, useFeatureFlag } from '@bonusly/who-am-i';

import { buildEmailRecipient, getInviteEmailRecipients, isInviteEmailRecipient } from '../utils/invite-emails';

type UseInviteConfirmationArgs = {
	/** Actually submits the recognition (`config.onSubmit` from `useGiveBox`). */
	submit: (action: ActionData) => void;
	isSubmitting: boolean;
	/** Surfaces the "invite by email unavailable" error in the composer. */
	showDisabledError: () => void;
};

/**
 * Owns the confirm-invitation step of the give flow: intercepts submits that
 * include invite-email recipients, holds the pending action while the user
 * confirms, and emits the RR-267 funnel events.
 *
 * `Confirmation Cancelled` fires only on explicit user dismissal (`cancel`);
 * the success path must close via `close` so a confirmed give never emits
 * Cancelled alongside the enriched Recognition Given completion event.
 */
export const useInviteConfirmation = ({ submit, isSubmitting, showDisabledError }: UseInviteConfirmationArgs) => {
	const { track } = useAnalytics();
	const isTrial = !!useCurrentCompany()?.isTrial;
	const inviteByEmailEnabled = useFeatureFlag('plg-invite-by-email');
	const modal = useDisclosure();
	const [pendingInviteAction, setPendingInviteAction] = useState<ActionData | null>(null);
	const [inviteEmails, setInviteEmails] = useState<string[]>([]);

	// Set synchronously in `confirm` because `isSubmitting` (React Query) only
	// flips on the next render — without this, Esc/X in that window would emit
	// Cancelled for a give that then also completes.
	const hasConfirmedRef = useRef(false);

	// Unlock when a confirmed submit settles with the modal still open (i.e.
	// it failed) so the user can dismiss again.
	const wasSubmittingRef = useRef(isSubmitting);
	useEffect(() => {
		if (wasSubmittingRef.current && !isSubmitting) {
			hasConfirmedRef.current = false;
		}
		wasSubmittingRef.current = isSubmitting;
	}, [isSubmitting]);

	/** Silent close — no analytics. The success path must use this. */
	const close = useCallback(() => {
		hasConfirmedRef.current = false;
		setPendingInviteAction(null);
		modal.onClose();
	}, [modal]);

	/** Explicit user dismissal (Cancel button, X, Esc, overlay). */
	const cancel = useCallback(() => {
		// Lock the modal once the user confirmed and while the submit is in
		// flight; otherwise a dismissal in that window would emit Cancelled for
		// a give that also lands in the completion leg of the funnel.
		if (isSubmitting || hasConfirmedRef.current) return;

		track(AnalyticsEvent.InviteByEmail.ConfirmationCancelled, {
			inviteCount: inviteEmails.length,
			hasAmount: (pendingInviteAction?.totalAmount ?? 0) > 0,
			isTrial,
			source: 'give_box',
		});
		close();
	}, [close, inviteEmails.length, isSubmitting, isTrial, pendingInviteAction, track]);

	const submitWithConfirmation = useCallback(
		(action: ActionData) => {
			const inviteRecipients = getInviteEmailRecipients(action);
			if (!inviteByEmailEnabled && inviteRecipients.length > 0) {
				showDisabledError();
				return;
			}

			if (inviteRecipients.length === 0) {
				submit(action);
				if (modal.isOpen) {
					close();
				}
				return;
			}

			track(AnalyticsEvent.InviteByEmail.ConfirmationShown, {
				inviteCount: inviteRecipients.length,
				hasAmount: (action.totalAmount ?? 0) > 0,
				isTrial,
				source: 'give_box',
			});
			hasConfirmedRef.current = false;
			setPendingInviteAction(action);
			setInviteEmails(inviteRecipients);
			modal.onOpen();
		},
		[close, inviteByEmailEnabled, isTrial, modal, showDisabledError, submit, track]
	);

	const confirm = useCallback(() => {
		// Same lock as `cancel`: a second Confirm click before `isSubmitting`
		// flips would fire a duplicate give.
		if (isSubmitting || hasConfirmedRef.current) return;

		if (!pendingInviteAction) {
			close();
			return;
		}

		// Safety check only. This should not happen because the modal only opens
		// when there are invite-email recipients in the recognition composer.
		if (inviteEmails.length === 0) {
			close();
			return;
		}

		const nonEmailRecipients = (pendingInviteAction.recipients ?? []).filter((recipient) => !isInviteEmailRecipient(recipient));
		const mergedEmailRecipients = inviteEmails.map(buildEmailRecipient);

		hasConfirmedRef.current = true;
		submit({
			...pendingInviteAction,
			recipients: [...nonEmailRecipients, ...mergedEmailRecipients],
		});
	}, [close, inviteEmails, isSubmitting, pendingInviteAction, submit]);

	return {
		isOpen: modal.isOpen,
		inviteEmails,
		submitWithConfirmation,
		confirm,
		cancel,
		close,
	};
};
